import { useState, useEffect } from "react";
import { notify } from "../../../shared/utils/utils";
import { fetchAuthSession } from "aws-amplify/auth";

const MAX_SECONDS_RESEND = 30;

function getConfirmEmailErrorMessage(message, fallback) {
  const normalizedMessage = String(message || "").toLowerCase();

  if (
    normalizedMessage.includes("invalid") ||
    normalizedMessage.includes("incorrect") ||
    normalizedMessage.includes("expired") ||
    normalizedMessage.includes("code")
  ) {
    return "Código inválido ou expirado.";
  }

  return fallback;
}

export const useConfirmEmail = (email, onSuccess) => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [timeLeft, setTimeLeft] = useState(0);

  /* ================================
     TIMER DE REENVIO
  =================================== */
  useEffect(() => {
    if (timeLeft <= 0) return;

    const interval = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [timeLeft]);

  /* ================================
     VERIFICAR CÓDIGO
  =================================== */
  const verifyCode = async () => {
    setError("");

    if (code.length !== 6) {
      setError("Por favor, insira um código de 6 dígitos.");
      return;
    }

    try {
      setLoading(true);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_AWS_URL}/auth/confirm-email`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, code }),
        }
      );

      const data = await response.json();

      if (data.status !== "success") {
        setError(getConfirmEmailErrorMessage(data.message, "Não foi possível verificar o código."));
        return;
      }

      /* 🔐 FORÇA REFRESH REAL NO COGNITO */
      const session = await fetchAuthSession({ forceRefresh: true });

      const idToken = session.tokens?.idToken?.toString();
      const accessToken = session.tokens?.accessToken?.toString();
      const refreshToken = session.tokens?.refreshToken?.toString();

      if (!idToken || !accessToken) {
        throw new Error("Erro ao renovar sessão.");
      }

      // Atualiza storage com tokens novos
      localStorage.setItem("jwt_token", accessToken);
      localStorage.setItem("idToken", idToken);
      if (refreshToken) {
        localStorage.setItem("refreshToken", refreshToken);
      }

      notify("Success", "Seu e-mail foi verificado com sucesso!", true);

      if (onSuccess) onSuccess();

    } catch (err) {
      console.error(err);
      setError("Erro ao verificar o código.");
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  /* ================================
     REENVIAR CÓDIGO
  =================================== */
  const resendCode = async () => {
    if (timeLeft > 0) return;

    try {
      setLoading(true);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_AWS_URL}/auth/confirm-email`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );

      const data = await response.json();

      if (data.status === "success") {
        notify("Success", "Código reenviado com sucesso!", true);
        setTimeLeft(MAX_SECONDS_RESEND);
      } else {
        setError(getConfirmEmailErrorMessage(data.message, "Não foi possível reenviar o código."));
      }

    } catch (err) {
      console.error(err);
      setError("Erro ao reenviar código.");
    } finally {
      setLoading(false);
    }
  };

  return {
    code,
    setCode,
    loading,
    error,
    timeLeft,
    verifyCode,
    resendCode,
  };
};
